import { apiClient } from './api-client';
import type {
  Ticket,
  Conversation,
  Message,
  PaginatedResponse,
  PaginationParams,
  TicketOriginOption,
  TicketOriginType,
} from '@centraoneplus/shared-types';

export interface HistoryEntry {
  id: string;
  eventType: string;
  title: string;
  description?: string;
  actorId?: string;
  actorName?: string;
  actorType: string;
  customerId?: string;
  ticketId?: string;
  metadata: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
}

export type AssigneeMatch = 'primary' | 'secondary' | 'any';

/** A secondary agent as the /secondary-assignees endpoints return it. */
export interface SecondaryAssignee {
  userId: string;
  name: string;
  email: string | null;
  addedById: string | null;
  createdAt: string;
}

/** An access request to a ticket, as /access-requests returns it. */
export interface TicketAccessRequest {
  id: string;
  ticketId: string;
  requesterId: string;
  requesterName: string;
  requesterEmail: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason: string | null;
  decidedById: string | null;
  decidedByName: string | null;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

/** Whether the caller may ask for access to a ticket they cannot open. */
export interface MyTicketAccessState {
  hasAccess: boolean;
  mentioned: boolean;
  canRequest: boolean;
  blockedReason: 'not_mentioned' | 'pending' | 'rejected' | 'access_removed' | null;
  request: TicketAccessRequest | null;
}

export interface TicketFilters extends PaginationParams {
  status?: string | string[];
  priority?: string | string[];
  assigneeId?: string;
  /** Comma-separated agent UUIDs for the multi-select assignee filter. */
  assigneeIds?: string;
  unassigned?: boolean;
  /** Widen the assignee filter to ALSO include unassigned tickets (OR). */
  includeUnassigned?: boolean;
  /** Which role the assignee filter matches: the assignee, a secondary agent, or either. */
  assigneeMatch?: AssigneeMatch;
  /**
   * Include backtracked tickets in the list. The list endpoint hides
   * backtracked tickets by default; analytics totals include them, so the
   * report export must opt in to match the on-screen "Total Tickets" (#10122).
   */
  includeBacktracked?: boolean;
  teamId?: string;
  pipelineId?: string;
  stageId?: string;
  ticketTypeId?: string;
  /** Comma-separated ticket type UUIDs for the multi-select ticket-type filter. */
  ticketTypeIds?: string;
  /** Comma-separated ticket sources (origin slugs) for the Source filter. */
  origin?: string;
  originType?: TicketOriginType;
  channel?: string | string[];
  search?: string;
  tags?: string[];
  createdAfter?: string;
  createdBefore?: string;
  slaBreached?: boolean;
  customerId?: string;
  contactEmail?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateTicketDto {
  subject: string;
  description?: string;
  priority?: string;
  channel: string;
  customerId?: string;
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  tags?: string[];
  customFields?: Record<string, any>;
  pipelineId?: string;
  stageId?: string;
  ticketTypeId?: string;
  // Optional explicit assignee chosen at creation. When omitted the backend
  // falls back to its routing/auto-assignment (or agent self-assign).
  assigneeId?: string;
}

export interface UpdateTicketDto {
  subject?: string;
  contactPhone?: string;
  description?: string;
  status?: string;
  priority?: string;
  assigneeId?: string | null;
  teamId?: string | null;
  tags?: string[];
  customFields?: Record<string, any>;
  ticketTypeId?: string | null;
  // Moving a ticket to a different pipeline. The backend relocates the ticket to
  // the new pipeline's first stage (or keeps the stage if it belongs to the new
  // pipeline) and syncs the status.
  pipelineId?: string | null;
  // Explicit stage move within the current pipeline (edit page's Stage selector).
  // The backend records a stage-change entry + a stage-history transition and
  // syncs the status from the stage's mapping.
  stageId?: string | null;
}

export interface CreateMessageDto {
  content: string;
  contentType?: 'text' | 'html' | 'markdown';
  isInternal?: boolean;
  attachments?: Array<{
    name: string;
    url: string;
    mimeType?: string;
    size?: number;
  }>;
  /** Teammates @mentioned in an internal note (drives the mention notification). */
  mentions?: Array<{ userId: string; userName: string }>;
  /**
   * Email reply recipients. Omit `to` to reply to the ticket contact only
   * (the original single-recipient behaviour). Never sent with internal notes.
   */
  to?: string[];
  cc?: string[];
}

/** An email reply held for its undo window (see sendMessageWithUndo). */
export interface UndoSendOperation {
  operationId: string;
  status: 'preparing' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';
  /** When the email goes out (server time, ISO). */
  deadline: string | null;
  undoSeconds: number;
  transport: string | null;
  /** The ticket message, once the email has been sent and recorded. */
  ticketMessageId: string | null;
  errorCode?: string;
  error?: string;
}

export type SendWithUndoResult =
  | { mode: 'scheduled'; operation: UndoSendOperation }
  | {
      mode: 'sent';
      message: Message;
      /** The reply went out immediately because undo was unavailable. */
      undoUnavailable: boolean;
      reason?: string;
    };

// Holding a reply creates the Outlook draft and uploads its attachments first.
const SEND_WITH_UNDO_TIMEOUT_MS = 150_000;

export interface TicketWithConversation extends Ticket {
  conversation?: Conversation;
  customer?: {
    id: string;
    name?: string;
    email: string;
  };
  assignee?: {
    id: string;
    name: string;
    email?: string;
  };
}

class TicketService {
  private readonly basePath = '/tickets';

  /**
   * Kanban board: one HTTP call returns all stages' tickets + counts in a
   * single round-trip. Replaces the N-stage parallel fan-out to ease DB
   * connection-pool pressure.
   */
  async getBoard(
    filters?: TicketFilters & { perStageLimit?: number },
  ): Promise<{
    ticketsByStage: Record<string, Ticket[]>;
    totalsByStage: Record<string, number>;
  }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, String(v)));
        } else {
          params.append(key, String(value));
        }
      });
    }
    const response = await apiClient.get<{
      ticketsByStage: Record<string, Ticket[]>;
      totalsByStage: Record<string, number>;
    }>(`${this.basePath}/board?${params.toString()}`);
    return response.data;
  }

  async getTickets(filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(key, v));
          } else {
            params.append(key, String(value));
          }
        }
      });
    }

    const response = await apiClient.get<PaginatedResponse<Ticket>>(
      `${this.basePath}?${params.toString()}`
    );
    return response.data;
  }

  /**
   * The tenant's distinct ticket sources (product / channel / agent), for the
   * list's Source filter. Sorted by ticket count, most common first.
   */
  async getOrigins(): Promise<TicketOriginOption[]> {
    const response = await apiClient.get<{ data?: TicketOriginOption[] }>(
      `${this.basePath}/origins`,
    );
    return response.data?.data ?? [];
  }

  async getTicket(id: string): Promise<TicketWithConversation> {
    const response = await apiClient.get<TicketWithConversation>(`${this.basePath}/${id}`);
    return response.data;
  }

  async createTicket(dto: CreateTicketDto): Promise<Ticket> {
    const response = await apiClient.post<Ticket>(this.basePath, dto);
    return response.data;
  }

  async updateTicket(id: string, dto: UpdateTicketDto): Promise<Ticket> {
    const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}`, dto);
    return response.data;
  }

  async deleteTicket(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }

  async assignTicket(id: string, assigneeId: string | null): Promise<Ticket> {
    if (assigneeId) {
      const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/assign`, {
        assigneeId,
      });
      return response.data;
    } else {
      const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/unassign`);
      return response.data;
    }
  }

  /** Add agents to work the ticket alongside its assignee. Returns the full list. */
  async addSecondaryAssignees(id: string, userIds: string[]): Promise<SecondaryAssignee[]> {
    const response = await apiClient.post<SecondaryAssignee[]>(
      `${this.basePath}/${id}/secondary-assignees`,
      { userIds },
    );
    return response.data;
  }

  /** Remove a secondary agent (or leave the ticket yourself). Returns the remaining list. */
  async removeSecondaryAssignee(id: string, userId: string): Promise<SecondaryAssignee[]> {
    const response = await apiClient.delete<SecondaryAssignee[]>(
      `${this.basePath}/${id}/secondary-assignees/${userId}`,
    );
    return response.data;
  }

  // ── Access requests: a mentioned agent asks for a ticket they cannot open ──

  async getMyAccessRequest(id: string): Promise<MyTicketAccessState> {
    const response = await apiClient.get<MyTicketAccessState>(`${this.basePath}/${id}/access-requests/mine`);
    return response.data;
  }

  async requestAccess(id: string, reason?: string): Promise<TicketAccessRequest> {
    const response = await apiClient.post<TicketAccessRequest>(
      `${this.basePath}/${id}/access-requests`,
      reason ? { reason } : {},
    );
    return response.data;
  }

  async cancelAccessRequest(id: string, requestId: string): Promise<TicketAccessRequest> {
    const response = await apiClient.post<TicketAccessRequest>(
      `${this.basePath}/${id}/access-requests/${requestId}/cancel`,
    );
    return response.data;
  }

  /** Approvers only (admins, owners, the ticket's team supervisor). */
  async getAccessRequests(id: string, status?: TicketAccessRequest['status']): Promise<TicketAccessRequest[]> {
    const response = await apiClient.get<TicketAccessRequest[]>(`${this.basePath}/${id}/access-requests`, {
      params: status ? { status } : undefined,
    });
    return response.data;
  }

  async decideAccessRequest(
    id: string,
    requestId: string,
    decision: 'approve' | 'reject',
    note?: string,
  ): Promise<TicketAccessRequest> {
    const response = await apiClient.post<TicketAccessRequest>(
      `${this.basePath}/${id}/access-requests/${requestId}/${decision}`,
      note ? { note } : {},
    );
    return response.data;
  }

  /**
   * Move a ticket to a team without naming an agent — e.g. out of a parent
   * team's queue into one of its sub-teams, where the pipeline's strategy
   * picks the agent.
   */
  async moveTicketToTeam(id: string, teamId: string): Promise<Ticket> {
    const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/assign`, { teamId });
    return response.data;
  }

  async updateStatus(id: string, status: string): Promise<Ticket> {
    const response = await apiClient.post<Ticket>(`${this.basePath}/${id}/status`, {
      status,
    });
    return response.data;
  }

  async updatePriority(id: string, priority: string): Promise<Ticket> {
    const response = await apiClient.post<Ticket>(`${this.basePath}/${id}/priority`, {
      priority,
    });
    return response.data;
  }

  async getConversation(ticketId: string): Promise<Conversation> {
    const response = await apiClient.get<Conversation>(
      `${this.basePath}/${ticketId}/conversation`
    );
    return response.data;
  }

  async getMessages(ticketId: string, conversationId: string): Promise<Message[]> {
    const response = await apiClient.get<{ data: Message[] } | Message[]>(
      `${this.basePath}/${ticketId}/conversations/${conversationId}/messages`
    );
    // Handle both {data: [...]} and [...] formats
    const data = response.data;
    return Array.isArray(data) ? data : data.data;
  }

  async sendMessage(
    ticketId: string,
    conversationId: string,
    dto: CreateMessageDto
  ): Promise<Message> {
    const response = await apiClient.post<Message>(
      `${this.basePath}/${ticketId}/conversations/${conversationId}/messages`,
      dto
    );
    return response.data;
  }

  /**
   * Send an email reply with an undo window. `operationId` identifies this send:
   * reuse it when retrying so a retry can never schedule a second email.
   */
  async sendMessageWithUndo(
    ticketId: string,
    conversationId: string,
    dto: CreateMessageDto & { operationId: string }
  ): Promise<SendWithUndoResult> {
    const response = await apiClient.post<SendWithUndoResult>(
      `${this.basePath}/${ticketId}/conversations/${conversationId}/messages/send-with-undo`,
      dto,
      { timeout: SEND_WITH_UNDO_TIMEOUT_MS }
    );
    return response.data;
  }

  async cancelUndoSend(operationId: string): Promise<UndoSendOperation> {
    const response = await apiClient.post<UndoSendOperation>(
      `${this.basePath}/undo-send/${operationId}/cancel`
    );
    return response.data;
  }

  async getUndoSendStatus(operationId: string): Promise<UndoSendOperation> {
    const response = await apiClient.get<UndoSendOperation>(`${this.basePath}/undo-send/${operationId}`);
    return response.data;
  }

  async updateMessage(
    ticketId: string,
    conversationId: string,
    messageId: string,
    content: string
  ): Promise<Message> {
    const response = await apiClient.patch<Message>(
      `${this.basePath}/${ticketId}/conversations/${conversationId}/messages/${messageId}`,
      { content }
    );
    return response.data;
  }

  async deleteMessage(
    ticketId: string,
    conversationId: string,
    messageId: string
  ): Promise<void> {
    await apiClient.delete(
      `${this.basePath}/${ticketId}/conversations/${conversationId}/messages/${messageId}`
    );
  }

  async addTags(id: string, tags: string[]): Promise<Ticket> {
    const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/tags`, { addTags: tags });
    return response.data;
  }

  async removeTags(id: string, tags: string[]): Promise<Ticket> {
    const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/tags`, { removeTags: tags });
    return response.data;
  }

  async mergeTickets(targetId: string, sourceIds: string[], reason?: string): Promise<void> {
    // The BFF merges ONE pair at a time (POST /bulk/merge with
    // { primaryTicketId, secondaryTicketId } — primary is kept, secondary is
    // closed and marked merged). Fold the selection by merging each secondary
    // into the primary; sequential so concurrent writes don't race on the
    // same primary ticket.
    for (const secondaryTicketId of sourceIds) {
      await apiClient.post('/bulk/merge', {
        primaryTicketId: targetId,
        secondaryTicketId,
        // Optional — the BFF forwards it as mergeNote, which ticket-service
        // posts as an internal note on the primary. Omitted when blank.
        ...(reason ? { reason } : {}),
      });
    }
  }

  async moveToStage(
    id: string,
    pipelineId: string,
    stageId: string,
    comment?: string
  ): Promise<Ticket> {
    const response = await apiClient.patch<Ticket>(`${this.basePath}/${id}/stage`, {
      pipelineId,
      stageId,
      comment,
    });
    return response.data;
  }

  async getStageHistory(id: string): Promise<Array<{
    id: string;
    fromStageName: string | null;
    toStageName: string;
    changedAt: string;
    timeInPreviousStage: number | null;
    comment: string | null;
  }>> {
    const response = await apiClient.get(`${this.basePath}/${id}/stage-history`);
    return response.data;
  }

  // Backtrack methods

  async getBacktrackedTickets(filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
    const response = await apiClient.get(`${this.basePath}/backtrack`, { params: filters });
    return response.data;
  }

  /**
   * Archive size for this scope, plus (when the service sends it) one count per
   * priority across the WHOLE archive — not just the page the screen fetched.
   */
  async getBacktrackCount(): Promise<{ count: number; byPriority?: Record<string, number> }> {
    const response = await apiClient.get(`${this.basePath}/backtrack/count`);
    return response.data;
  }

  async reopenTicket(
    id: string,
    body?: { reason: string; stageId?: string },
  ): Promise<Ticket> {
    const response = await apiClient.patch(`${this.basePath}/${id}/reopen`, body);
    return response.data;
  }

  /**
   * Safety-net: re-link any email attachments to this ticket. Called by the
   * ticket detail page when the attachments panel comes back empty, to
   * cover races where the event-driven linker hasn't caught up yet.
   */
  async reconcileAttachments(
    id: string,
  ): Promise<{ ticketId: string; linked: number }> {
    const response = await apiClient.post<{ ticketId: string; linked: number }>(
      `${this.basePath}/${id}/attachments/reconcile`,
      {},
    );
    return response.data;
  }

  /**
   * Record an attachment add/remove in the ticket history. Best-effort — the
   * file-service upload/delete is the source of truth; this only annotates the
   * history timeline, so failures are swallowed and never block the UX.
   */
  async recordAttachmentActivity(
    id: string,
    action: 'added' | 'removed',
    files: Array<{ name?: string; fileId?: string }>,
  ): Promise<void> {
    try {
      await apiClient.post(`${this.basePath}/${id}/attachments/activity`, { action, files });
    } catch {
      // non-critical
    }
  }

  // History methods

  async getHistory(
    ticketId: string,
    params?: {
      page?: number;
      limit?: number;
      types?: string[];
      startDate?: string;
      endDate?: string;
    },
  ): Promise<{ data: HistoryEntry[]; total: number; page: number; limit: number; totalPages: number }> {
    const queryParams: Record<string, string> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.types?.length) queryParams.types = params.types.join(',');
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    const response = await apiClient.get(`${this.basePath}/${ticketId}/history`, { params: queryParams });
    return response.data;
  }

  // Migration methods

  async assignUnassignedToPipeline(): Promise<{ assigned: number; total: number }> {
    const response = await apiClient.post(`${this.basePath}/migrate/assign-pipeline`);
    return response.data;
  }
}

export const ticketService = new TicketService();
